import type { AddToCartData, RemoveFromCartData } from '@/events/conversion';

/** Janela de interação para dedupe de ADD/REMOVE_FROM_CART / cross-detector (ms). */
export const CONVERSION_INTERACTION_WINDOW_MS = 500;

type CartLineDedupeData = {
  productId?: string;
  productName?: string;
  quantity?: number;
};

/**
 * Estratégia de dedupe para ADD_TO_CART / REMOVE_FROM_CART (Etapa 12J).
 *
 * Objetivo:
 * - Bloquear duplicidade na mesma interação (mesmo bucket).
 * - Permitir nova ação legítima após a janela (ex.: add → remove → add → remove).
 * - Não bloquear o mesmo productId para sempre.
 *
 * Chave (com productId):
 *   {TYPE}:product:{id}:qty:{n}:bucket:{t}
 *
 * Origin NÃO entra na chave quando há productId — assim selector + form
 * na mesma interação colidem corretamente.
 *
 * Sem productId:
 *   {TYPE}:name:{name}:qty:{n}:bucket:{t}  |  {TYPE}:interaction:{bucket}
 */
function buildCartLineDedupeKey(
  type: 'ADD_TO_CART' | 'REMOVE_FROM_CART',
  data: CartLineDedupeData | undefined,
  now: number,
  windowMs: number
): string {
  const bucket = getInteractionBucket(now, windowMs);
  const productId =
    typeof data?.productId === 'string' ? data.productId.trim() : '';
  const quantity = normalizeQuantity(data?.quantity);
  const productName =
    typeof data?.productName === 'string' ? data.productName.trim() : '';

  if (productId) {
    return `${type}:product:${productId}:qty:${quantity}:bucket:${bucket}`;
  }

  if (productName) {
    return `${type}:name:${productName}:qty:${quantity}:bucket:${bucket}`;
  }

  return `${type}:interaction:${bucket}`;
}

export function buildAddToCartDedupeKey(
  data: AddToCartData | undefined,
  now: number = Date.now(),
  windowMs: number = CONVERSION_INTERACTION_WINDOW_MS
): string {
  return buildCartLineDedupeKey('ADD_TO_CART', data, now, windowMs);
}

export function buildRemoveFromCartDedupeKey(
  data: RemoveFromCartData | undefined,
  now: number = Date.now(),
  windowMs: number = CONVERSION_INTERACTION_WINDOW_MS
): string {
  return buildCartLineDedupeKey('REMOVE_FROM_CART', data, now, windowMs);
}

export function getInteractionBucket(
  now: number = Date.now(),
  windowMs: number = CONVERSION_INTERACTION_WINDOW_MS
): number {
  const window = windowMs > 0 ? windowMs : CONVERSION_INTERACTION_WINDOW_MS;
  return Math.floor(now / window);
}

function normalizeQuantity(quantity: number | undefined): number {
  if (typeof quantity === 'number' && Number.isFinite(quantity)) {
    return quantity;
  }
  return 1;
}
